import {
  type Color,
  COORD_MAX,
  COORD_MIN,
  DIR_OFFSET,
  fileOf,
  KING_DIRS,
  KNIGHT,
  knightReaches,
  PAWN,
  pieceColor,
  pieceType,
  rankOf,
  REAIM_RADIUS,
  square
} from "src/engine/rules/board";
import type { GameState } from "src/engine/rules/commands";
import { hasCell, pieceAt, type Position } from "src/engine/rules/position";
import { CARD_TABLE } from "src/engine/rules/campaign/cards";
import { refuseBridge } from "src/engine/rules/campaign/bridges";
import { bestBeltRoute, canExtendBelt } from "src/engine/rules/industry/belts";
import { hasStructure } from "src/engine/rules/industry/state";
import { isAttack, isBash, type Move, moveFrom, moveTarget, moveTo } from "src/engine/rules/move";
import type { BoardHighlights } from "src/api";

import { beltPreview, previewSquares } from "./beltPreview";
import type { Aiming, Forging, Pending, Placing } from "./types";

/**
 * The neighbours a card would accept as the *second* square for this target.
 *
 * The Hydraulic Clasp and the IFF Spoofer are the two that need it — each is a
 * relationship between two squares — and this asks the card rather than
 * restating its rule, so the highlight and the reducer cannot disagree about
 * what is legal. A card that wants no source answers with an empty list for
 * every square, which is what keeps the one-square flow untouched.
 *
 * All eight neighbours, matching the cards' own geometric reading: a source
 * further away than a king steps is not "beside it" by any definition the
 * board shows.
 */
export function sourcesFor(
  card: (typeof CARD_TABLE)[keyof typeof CARD_TABLE],
  state: GameState,
  target: number
): number[] {
  return KING_DIRS.map((offset) => target + offset).filter(
    (source) => card.refuse(state, { target, source }) === null
  );
}
/**
 * Where a one-square bridge may go — every gap the ring holds that joins two banks.
 *
 * Filtered through `refuseBridge`, so the board cannot light a square the
 * reducer refuses (§26 C9). Longer chains need a drag gesture that does not
 * exist yet; until it does, this is the whole of what a click can lay.
 */
export function paveRing(state: GameState): number[] {
  const { minFile, maxFile, minRank, maxRank } = state.position.bounds;
  const out: number[] = [];
  for (let rank = minRank - 1; rank <= maxRank + 1; rank++) {
    for (let file = minFile - 1; file <= maxFile + 1; file++) {
      const at = ((rank + 512) << 10) | (file + 512);
      if (refuseBridge(state.position, state.industry, [at]) === null) out.push(at);
    }
  }
  return out;
}
/**
 * The moves the selection is actually offered.
 *
 * Gated on the *seat*, not on the selection. Selecting is looking and is
 * free at any time, but `legalMoves` generates for whoever is on the clock —
 * so with selection unconditional, a client seated as Black could click a
 * White piece on White's turn and be shown its moves. The board would move
 * locally, the server would refuse it, and the two would disagree until the
 * next message put them back. The net smoke caught exactly that.
 */
export function selectedMoves(
  moves: Move[],
  selected: number | undefined,
  controlsTurn: boolean,
  localSide: Color,
  position: Position
): Move[] {
  return selected === undefined || !controlsTurn
    ? []
    : moves.filter(
        (m) => moveFrom(m) === selected && pieceColor(pieceAt(position, selected)) === localSide
      );
}
/** Pawns the selected knight could turn — ours, within `REAIM_RADIUS` (§31 P1). */
export function reAimTargetsOf(
  state: GameState,
  selected: number | undefined,
  controlsTurn: boolean
): number[] {
  if (selected === undefined || !controlsTurn) return [];
  const knight = pieceAt(state.position, selected);
  if (
    !knight ||
    pieceColor(knight) !== state.position.turn ||
    pieceType(knight) !== KNIGHT
  ) {
    return [];
  }
  // `knightReaches` is the rule; the sweep is its bounding square, by
  // coordinates — a packed offset wraps at the file band.
  const out: number[] = [];
  const file = fileOf(selected);
  const rank = rankOf(selected);
  for (let dr = -REAIM_RADIUS; dr <= REAIM_RADIUS; dr++) {
    for (let df = -REAIM_RADIUS; df <= REAIM_RADIUS; df++) {
      const f = file + df;
      const r = rank + dr;
      if (f < COORD_MIN || f > COORD_MAX || r < COORD_MIN || r > COORD_MAX) continue;
      const sq = square(f, r);
      if (!knightReaches(selected, sq) || !hasCell(state.position, sq)) continue;
      const piece = pieceAt(state.position, sq);
      if (piece !== 0 && pieceColor(piece) === state.position.turn && pieceType(piece) === PAWN) {
        out.push(sq);
      }
    }
  }
  return out.sort((a, b) => a - b);
}
/**
 * With a run anchored, the far ends worth *marking*: every square along the
 * anchor's rank and file the run could reach in a straight line, stopping at
 * the first it could not — you cannot lay past an obstruction, so offering it
 * would lie.
 *
 * **An affordance, not the limit.** A run may be any shape that feeds through
 * itself, so any empty square completes one and the preview draws whatever
 * the click would lay, corner and all. What is marked is the straight
 * answer, which is the one worth pointing at; enumerating every bent end
 * would be a sweep of the board on every state change for a highlight
 * nobody reads square by square.
 *
 * It walks by `canExtendBelt` and offers by whether the square is *empty*,
 * and the two are no longer the same question: a run may pass under your own
 * pieces and over your own track, but a click on a piece selects it, so a
 * piece is somewhere a run goes *through* rather than somewhere it ends.
 */
export function lineEndsFrom(
  state: GameState,
  lineAnchor: number | undefined,
  localSide: Color
): number[] {
  if (lineAnchor === undefined) return [];
  const ends: number[] = [];
  for (const step of DIR_OFFSET) {
    for (let sq = lineAnchor + step; hasCell(state.position, sq); sq += step) {
      if (canExtendBelt(state.position, state.industry, sq, localSide) !== null) break;
      if (pieceAt(state.position, sq) === 0) ends.push(sq);
    }
  }
  return ends;
}
/**
 * The run being drawn, as the parts it would lay.
 *
 * Both ways of drawing one, because both used to be invisible until they
 * were committed: a freehand drag, and an anchored line reaching for the
 * square under the pointer. The line half is why `hovered` exists — a run
 * whose far end is wherever you happen to be looking has no other way to
 * know where it currently ends.
 */
export function beltPreviewFor(
  state: GameState,
  drawing: boolean,
  path: number[],
  lineAnchor: number | undefined,
  hovered: number | undefined,
  localSide: Color
) {
  if (drawing) return beltPreview(state, path, localSide);
  if (lineAnchor === undefined || hovered === undefined) return [];
  // Exactly where the click would lay, and exactly the route it would take:
  // `bestBeltRoute` is what the reducer runs, so the ghost cannot promise a
  // corner the lane then turns the other way round. Gated on the same thing
  // the click is — an empty square — because a click on a piece selects it.
  if (pieceAt(state.position, hovered) !== 0) return [];
  const route = bestBeltRoute(state.position, state.industry, lineAnchor, hovered, localSide);
  if (!route) return [];
  return beltPreview(state, route.squares, localSide, true);
}
/**
 * A card's candidates, split by *where they are* rather than by which card.
 *
 * A square on the board wears the capture ring, which is a ring around the
 * thing standing there; a square beyond the frontier wears the put-down
 * marker, which is what `spawns` has always meant. §26 C10's four marks are
 * the second kind — a ring drawn on empty ground past the edge says nothing
 * is there, which is true and is not the message. A property of the square,
 * so a later card that aims off the board needs nothing here.
 */
export function aimMarksOf(state: GameState, aiming: Aiming | undefined) {
  const at = aiming ? aiming.at : [];
  const board = (sq: number) =>
    hasCell(state.position, sq) || hasStructure(state.industry, sq);
  return { on: at.filter(board), off: at.filter((sq) => !board(sq)) };
}

/**
 * Everything the renderer is asked to draw, gathered in one place.
 *
 * Taken as one object rather than seventeen arguments so the call site reads
 * as the memo it is; the object is built inside that memo, so nothing new is
 * allocated on a render that did not change any of them.
 */
export interface HighlightInputs {
  selected: number | undefined;
  movesFromSelected: Move[];
  reAimTargets: number[];
  pending: Pending | undefined;
  lineAnchor: number | undefined;
  lineEnds: number[];
  path: number[];
  drawing: boolean;
  preview: ReturnType<typeof beltPreview>;
  forging: Forging | undefined;
  placing: Placing | undefined;
  /**
   * The square under the pointer, for the one thing the ring can say during a
   * forge — see `selected` below.
   */
  hovered: number | undefined;
  aiming: Aiming | undefined;
  aimMarks: ReturnType<typeof aimMarksOf>;
  paving: boolean;
  paveTargets: number[];
  /** The rest of what the selected square is part of (`ground.ts`'s `marks`). */
  extent: readonly number[];
}

/**
 * Nothing lit at all.
 *
 * Frozen and shared, so a board with no marks on it is one identity rather
 * than a fresh object per render — which is what `BoardCanvas`' highlight
 * effect compares on. Used while a step of history is on screen (§29 U4).
 */
export const NO_HIGHLIGHTS: BoardHighlights = Object.freeze({});

export function buildHighlights({
  selected,
  movesFromSelected,
  reAimTargets,
  pending,
  lineAnchor,
  lineEnds,
  path,
  drawing,
  preview,
  forging,
  placing,
  hovered,
  aiming,
  aimMarks,
  paving,
  paveTargets,
  extent
}: HighlightInputs): BoardHighlights {
  // Built over packed numbers, as every model here speaks; the contract brands
  // them. One cast at the one door, until the models are branded on day 6.
  return <BoardHighlights>(<unknown>{
    /**
     * **While a forge is armed the ring follows the pointer to the machine
     * that would pay.**
     *
     * The hotbar lights the union over every foundry of yours, and which one
     * leads is a property of the *square* (`forging.leads`) — so the board was
     * showing squares beside a rook the player had not been thinking about,
     * with nothing to say why. Reported from play as mint squares appearing
     * next to the wrong piece; the king is a foundry too (`isFoundry`), which
     * is what makes it surprising rather than merely unexplained.
     *
     * The lit squares are still the whole offer. This only answers "who is
     * making this one", and only about the square being pointed at — a ring on
     * every lead at once would be a second set of marks competing with the
     * spawn markers for the same message.
     *
     * A hover off the lit set resolves to nothing and the ring goes out, which
     * is why the `Map` lookup carries the test rather than a membership check
     * against `forging.at`: one definition of "is this square in the offer",
     * and it is the one that also names the payer.
     */
    selected: forging
      ? (hovered === undefined ? undefined : forging.leads?.get(hovered)) ??
        forging.square
      : drawing
      ? path[0]
      : lineAnchor ?? selected,
    // While a run is being drawn the wash is the squares that would actually
    // take a part — which is `preview`, not the gesture. They differ wherever
    // the pointer has crossed a heap or an enemy piece, and lighting a square
    // the reducer is about to skip is the board promising something it will
    // not do.
    // **Movement only.** A forge's destinations and the ground a tile would
    // pave both used to arrive here, which made this "every square this
    // click could act on" and drew a mint with the walk-here marker. They
    // are `spawns` now; what is left is squares the selected piece may go to,
    // plus the two construction previews, which are neither a move nor a
    // spawn and keep the plain marker they always had.
    legalMoves: aiming || forging || placing
      ? []
      : drawing
        ? previewSquares(preview)
        : lineAnchor !== undefined
        ? lineEnds
        // **`isAttack`, not `isCapture`** (§27): a bash takes nothing, so
        // it carries no capture flag — and drawn as a quiet move it would
        // mark the square the attacker *stops* on, one short of the piece it
        // is hitting, which is a marker on empty ground with nothing there
        // to click.
        : movesFromSelected.filter((m) => !isAttack(m)).map(moveTo),
    // Where a piece would be *put down*: a foundry's mint squares, or the
    // ground outside the board while there are Free Space tiles to place.
    //
    // Paving is last in the chain rather than first, the same ordering the
    // old `legalMoves` used: a selection, a forge and a drawn run are all
    // *this* click, and paving is a standing offer that must not cover them.
    spawns: forging
      ? forging.at
      : placing
      ? placing.at
      // A card aimed off the board — §26 C10's marks — before paving, which
      // is the standing offer the click handler now also stands down while
      // something is being aimed. The two agreeing is the point.
      : aiming
      ? aimMarks.off
      : paving && !drawing && lineAnchor === undefined
      ? paveTargets
      : [],
    beltPreview: preview,
    // A card's candidates are squares with *pieces* on them, so they use the
    // capture highlight — a ring around the piece — rather than the legal
    // move marker, which draws on the tile and is hidden under whatever is
    // standing there. A target you cannot see is a target you cannot pick.
    captures: aiming
      ? aimMarks.on
      : lineAnchor !== undefined
      ? []
      // Ringed at the piece being hit, which for a bash is `shoved` rather
      // than where the attacker ends up — the same square `findLegalMove`
      // resolves the command on, so what is lit is what a click will do.
      //
      // **Only the lethal ones wear red** (§27): a blow short of the
      // target's health shoves instead of taking, and it gets the amber
      // ring below so the board never promises a capture it will not make.
      : movesFromSelected.filter((m) => isAttack(m) && !isBash(m)).map(moveTarget),
    shoves:
      aiming || lineAnchor !== undefined
        ? []
        : movesFromSelected.filter(isBash).map(moveTarget),
    reAimTargets,
    // What the selected ground is part of — and nothing while another gesture
    // owns the board, because those marks are about the next click and this is
    // about the last one.
    extent:
      aiming || forging || placing || drawing || lineAnchor !== undefined ? [] : extent,
    directionPicker: pending
      ? pending.kind === "re-aim"
        ? pending.pawn
        : pending.square
      : null
  });
}
